// src/generators/dzial2_algebra/temat5_zadania_uzupelniajace_maturalne/
// podtemat3_maturalne_otwarte_types/MaturaQuadraticInequalityOpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";

import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";

import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";

/* ============================================================================
 * HELPERY
 * ========================================================================== */

const tol = 1e-6;
const eq = (a, b) => Math.abs(a - b) <= tol;

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const SIGN_LATEX = {
  lt: "<",
  le: "\\le",
  gt: ">",
  ge: "\\ge",
};

function quadraticLatex(a, b, c) {
  const ax = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  const bx =
    b === 0
      ? ""
      : b === 1
      ? "+ x"
      : b === -1
      ? "- x"
      : b > 0
      ? `+ ${b}x`
      : `- ${Math.abs(b)}x`;
  const cx =
    c === 0
      ? ""
      : c > 0
      ? `+ ${c}`
      : `- ${Math.abs(c)}`;
  return `${ax} ${bx} ${cx}`.replace(/\s+/g, " ").trim();
}

function linearLatex(p, q) {
  const px =
    p === 0
      ? ""
      : p === 1
      ? "x"
      : p === -1
      ? "-x"
      : p > 0
      ? `${p}x`
      : `-${Math.abs(p)}x`;
  const qq =
    q === 0
      ? ""
      : q > 0
      ? `+ ${q}`
      : `- ${Math.abs(q)}`;
  const s = `${px} ${qq}`.replace(/\s+/g, " ").trim();
  return s.length ? s : "0";
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

function toMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs + 1e-12);
  const frac = abs - intPart;
  if (frac < 1e-12) return `${sign}${intPart}`;
  for (let d = 1; d <= 20; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\dfrac{${num}}{${den}}`
        : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// czytamy liczbę z FractionInput / RootFractionInput / stringa
function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeUserNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;

  if (typeof v === "string") {
    const s = normalizeUserNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

/** zawsze zwraca string dla pola S (przedział) */
function getIntervalRaw(userInput) {
  const val = userInput?.S;
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object" && typeof val.raw === "string") {
    return val.raw;
  }
  return String(val);
}

/** wyciąga surowy tekst z FractionInput-a */
function getFractionRaw(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.raw === "string") return v.raw ?? "";
  return "";
}

function solutionFor({ a, r1, r2, kind }) {
  const inclusive = kind === "le" || kind === "ge";
  const wantsPositive = kind === "gt" || kind === "ge";
  const outside = wantsPositive ? a > 0 : a < 0;

  return outside
    ? {
        kind: "outside",
        inclusive,
        left: { L: -Infinity, R: r1, Lclosed: false, Rclosed: inclusive },
        right: { L: r2, R: Infinity, Lclosed: inclusive, Rclosed: false },
      }
    : {
        kind: "between",
        inclusive,
        seg: { L: r1, R: r2, Lclosed: inclusive, Rclosed: inclusive },
      };
}

function intervalsEqual(expectedSolution, parsed) {
  if (!parsed || parsed.kind !== "intervals") return false;

  const expected =
    expectedSolution.kind === "outside"
      ? [expectedSolution.left, expectedSolution.right]
      : [expectedSolution.seg];
  if (parsed.intervals.length !== expected.length) return false;

  return expected.every((wanted, index) => {
    const received = parsed.intervals[index];
    const leftMatches =
      wanted.L === -Infinity
        ? received.left === -Infinity
        : eq(received.left, wanted.L);
    const rightMatches =
      wanted.R === Infinity
        ? received.right === Infinity
        : eq(received.right, wanted.R);

    return (
      leftMatches &&
      rightMatches &&
      received.leftClosed === wanted.Lclosed &&
      received.rightClosed === wanted.Rclosed
    );
  });
}

/* ============================================================================
 * GENERATOR
 * ========================================================================== */

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 9:
 *   -3x^2 > 6x - 9   ⟺   -3x^2 - 6x + 9 > 0   →   x ∈ (-3, 1)
 * (a = -3 < 0 — parabola ramionami w dół, rozwiązanie „pomiędzy”.)
 */
function createAugust2025OriginalTask() {
  return {
    type: "MaturaQuadraticInequalityOpenTask",
    data: {
      a: -3,
      B: -6,
      C: 9,
      disc: 144,
      sqrtDisc: 12,
      r1: -3,
      r2: 1,
      kind: "gt",
      prettyLatex: "-3x^2\\;>\\;6x - 9",
      standardLatex: "-3x^2 - 6x + 9\\;>\\;0",
      maxPoints: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 9,
    },
  };
}

function createMay2026OriginalTask() {
  return {
    type: "MaturaQuadraticInequalityOpenTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2026_MAJ",
      taskNumber: "10",
    },
    data: {
      a: 3,
      B: -2,
      C: -8,
      disc: 100,
      sqrtDisc: 10,
      r1: -4 / 3,
      r2: 2,
      kind: "ge",
      prettyLatex: "3x^2+4x\\ge 6x+8",
      standardLatex: "3x^2-2x-8\\ge0",
      maxPoints: 2,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2026_MAJ"
  ) {
    return createMay2026OriginalTask();
  }

  if (generationOptions.variant === "original") {
    return createAugust2025OriginalTask();
  }

  // ~50% z nieostrym znakiem
  const inclusive = Math.random() < 0.5;

  let a = 0;
  while (a === 0 || Math.abs(a) === 1) a = randInt(-5, 5);

  let b, c, p, q, B, C, disc, sqrtDisc;

  while (true) {
    b = randInt(-8, 8);
    c = randInt(-10, 10);
    p = randInt(-6, 6);
    q = randInt(-10, 10);

    if (p === 0 && q === 0) continue; // prawa strona nie może być 0

    B = b - p;
    C = c - q;

    if (B === 0 || C === 0) continue; // pełny trójmian

    disc = B * B - 4 * a * C;
    if (disc <= 0) continue;

    sqrtDisc = Math.trunc(Math.sqrt(disc));
    if (sqrtDisc * sqrtDisc !== disc) continue;

    break;
  }

  const rA = (-B - sqrtDisc) / (2 * a);
  const rB = (-B + sqrtDisc) / (2 * a);
  const r1 = Math.min(rA, rB);
  const r2 = Math.max(rA, rB);

  // W trzech z czterech zarejestrowanych oryginałów CKE rozwiązanie leży
  // pomiędzy miejscami zerowymi, a w jednym — na zewnątrz. Zachowujemy tę
  // proporcję, ale rzeczywiście ćwiczymy oba warianty.
  const wantsOutside = Math.random() < 0.25;
  const wantsPositive = wantsOutside ? a > 0 : a < 0;
  const kind = wantsPositive
    ? inclusive
      ? "ge"
      : "gt"
    : inclusive
    ? "le"
    : "lt";

  const prettyLatex = `${quadraticLatex(
    a,
    b,
    c
  )}\\;${SIGN_LATEX[kind]}\\;${linearLatex(p, q)}`;

  const standardLatex = `${quadraticLatex(
    a,
    B,
    C
  )}\\;${SIGN_LATEX[kind]}\\;0`;

  return {
    type: "MaturaQuadraticInequalityOpenTask",
    data: {
      a,
      B,
      C,
      disc,
      sqrtDisc,
      r1,
      r2,
      kind, // "lt", "le", "gt", "ge"
      prettyLatex,
      standardLatex,
      maxPoints: 2, // 1 pkt za pierwiastki, 1 pkt za przedział
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_9" },
  };
}

/* ============================================================================
 * TREŚĆ ZADANIA
 * ========================================================================== */

export function renderTask({ data }) {
  return (
    <div style={{ lineHeight: 1.5 }}>
      <p>Rozwiąż nierówność kwadratową i zapisz zbiór rozwiązań.</p>
      <BlockMath math={data.prettyLatex} />
    </div>
  );
}

/* ============================================================================
 * INPUT
 * ========================================================================== */

export function renderInput(_, userInput = {}, onChange, disabled) {
  const { x1 = "", x2 = "", S = "" } = userInput;

  return (
    <div className="flex flex-col gap-4">
      {/* Krok 1 – miejsca zerowe */}
      <div className="flex flex-col gap-2">
        <div>
          <b>Krok 1.</b> Oblicz miejsca zerowe trójmianu kwadratowego.
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <InlineMath math={"x_1\\;="} />
          <FractionInput
            label="x₁"
            value={x1}
            onChange={(val) => onChange({ ...userInput, x1: val })}
            disabled={disabled}
            placeholder="np. -3 lub -3/2"
          />

          <InlineMath math={"x_2\\;="} />
          <FractionInput
            label="x₂"
            value={x2}
            onChange={(val) => onChange({ ...userInput, x2: val })}
            disabled={disabled}
            placeholder="np. 1 lub 5/2"
          />
        </div>
      </div>

      {/* Krok 2 – zbiór rozwiązań */}
      <div className="flex flex-col gap-2">
        <div>
          <b>Krok 2.</b> Zapisz zbiór rozwiązań nierówności.
        </div>

        <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
          <div style={{ lineHeight: 1 }}>
            <InlineMath math={"x\\;\\in"} />
          </div>
          <IntervalInput
            value={S}
            onChange={(val) => onChange({ ...userInput, S: val })}
            disabled={disabled}
            asString
            previewWithX
            placeholder="np. (-∞; -3] ∪ [1; ∞) albo (-3; 1)"
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * RENDER ODP. UŻYTKOWNIKA
 * ========================================================================== */

export function renderUserAnswer(userInput = {}) {
  const x1 = userInput?.x1;
  const x2 = userInput?.x2;

  const x1Raw = getFractionRaw(x1);
  const x2Raw = getFractionRaw(x2);

  const Sraw = getIntervalRaw(userInput); // ZAWSZE string

  const x1Latex =
    x1Raw &&
    (formatFracPreview(x1Raw) ||
      formatNumLatex(String(parseNumberFromAny(x1)), ""));
  const x2Latex =
    x2Raw &&
    (formatFracPreview(x2Raw) ||
      formatNumLatex(String(parseNumberFromAny(x2)), ""));
  const SLat = Sraw.trim()
    ? intervalToLatex(Sraw, { withX: true })
    : "";

  return (
    <div className="flex flex-col gap-1">
      <div>
        {x1Latex || x2Latex ? (
          <InlineMath
            math={`x_1 = ${x1Latex || "\\;?"},\\; x_2 = ${x2Latex || "\\;?"}`}
          />
        ) : (
          <i>(brak odpowiedzi dla miejsc zerowych)</i>
        )}
      </div>
      <div>
        {SLat ? (
          <InlineMath math={SLat} />
        ) : (
          <i>(brak odpowiedzi dla zbioru rozwiązań)</i>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
 * KRÓTKA ODPOWIEDŹ
 * ========================================================================== */

export function renderAnswer({ data }) {
  const { r1, r2 } = data;
  const x1L = toMixedFractionLatex(r1);
  const x2L = toMixedFractionLatex(r2);
  const solution = solutionFor(data);
  const lBrace = solution.inclusive ? "[" : "(";
  const rBrace = solution.inclusive ? "]" : ")";
  const solutionLatex =
    solution.kind === "outside"
      ? `(-\\infty,\\; ${x1L}${rBrace}\\;\\cup\\; ${lBrace}${x2L},\\; \\infty)`
      : `${lBrace}${x1L},\\; ${x2L}${rBrace}`;

  return (
    <div
      style={{
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
        lineHeight: 1.4,
      }}
    >
      <div>
        <InlineMath math={`x_1 = ${x1L},\\; x_2 = ${x2L}`} />
      </div>
      <div>
        <InlineMath math={`x \\in ${solutionLatex}`} />
      </div>
    </div>
  );
}

/* ============================================================================
 * SPRAWDZANIE (2 pkt – miejsca zerowe + przedział)
 * ========================================================================== */

function checkRoots({ r1, r2 }, userInput) {
  const u1 = parseNumberFromAny(userInput?.x1);
  const u2 = parseNumberFromAny(userInput?.x2);
  if (!Number.isFinite(u1) || !Number.isFinite(u2)) return false;

  const [a1, a2] = [r1, r2].sort((x, y) => x - y);
  const [b1, b2] = [u1, u2].sort((x, y) => x - y);

  return eq(a1, b1) && eq(a2, b2);
}

function checkInterval(data, userInput) {
  const raw = getIntervalRaw(userInput); // zawsze string
  if (!raw.trim()) return false;

  const parsed = parseIntervalInput(raw);
  return intervalsEqual(solutionFor(data), parsed);
}

export function checkAnswer({ data }, userInput = {}) {
  let goodRoots = checkRoots(data, userInput);
  const goodInterval = checkInterval(data, userInput);

  // Jeśli użytkownik NIE podał x1 i x2 (puste pola),
  // ale podał poprawny przedział, to uznajemy komplet (2 pkt),
  // bo pierwiastki są "zawarte" w końcach przedziału.
  const x1Raw = getFractionRaw(userInput?.x1).trim();
  const x2Raw = getFractionRaw(userInput?.x2).trim();
  const rootsMissing = !x1Raw && !x2Raw;

  if (rootsMissing && goodInterval) {
    goodRoots = true;
  }

  // Scoring:
  // - jeśli przedział poprawny => 2 pkt (pełna odpowiedź)
  // - jeśli przedział błędny, ale pierwiastki poprawne => 1 pkt
  // - w pozostałych => 0 pkt
  const score = goodInterval ? 2 : goodRoots ? 1 : 0;

  const parts = {
    "miejsca zerowe trójmianu": goodRoots,
    "zbiór rozwiązań nierówności": goodInterval,
  };

  const rawS = getIntervalRaw(userInput);
  const parsedS = rawS.trim() ? parseIntervalInput(rawS) : null;
  const invalidOrder = !!parsedS && parsedS.kind === "invalid_order";
  const message = invalidOrder
    ? "Uwaga: przedział zapisano od większej do mniejszej liczby. Zapisuj przedziały od mniejszej do większej (lewy koniec ≤ prawy koniec)."
    : null;

  return {
    isCorrect: score === data.maxPoints,
    score,
    maxScore: data.maxPoints,
    parts,
    message,
    messageType: message ? "hint" : null,
  };
}

/* ============================================================================
 * WYJAŚNIENIA
 * ========================================================================== */

export function renderExplanation({ data }) {
  const { a, B, C, disc, sqrtDisc, r1, r2, kind, prettyLatex, standardLatex } =
    data;

  const inclusive = kind === "le" || kind === "ge";
  const aPos = a > 0;
  const solution = solutionFor(data);
  const armsTxt = aPos ? "ramiona do góry" : "ramiona w dół";
  const whereTxt =
    kind === "lt" || kind === "le"
      ? <>szukamy części wykresu poniżej osi <InlineMath math="Ox" /></>
      : <>szukamy części wykresu powyżej osi <InlineMath math="Ox" /></>;
  const inclusiveNote = inclusive
    ? <>Ponieważ nierówność jest nieostra (<InlineMath math="\leq" /> lub <InlineMath math="\geq" />), uwzględniamy również miejsca zerowe.</>
    : <>Ponieważ nierówność jest ostra (<InlineMath math="<" /> lub <InlineMath math=">" />), miejsca zerowe nie należą do rozwiązania.</>;

  const x1L = toMixedFractionLatex(r1);
  const x2L = toMixedFractionLatex(r2);

  const lBrace = inclusive ? "[" : "(";
  const rBrace = inclusive ? "]" : ")";
  const solLatex =
    solution.kind === "outside"
      ? String.raw`x\in (-\infty,\; ${x1L}${rBrace}\;\cup\; ${lBrace}${x2L},\; \infty)`
      : String.raw`x\in ${lBrace}${x1L},\; ${x2L}${rBrace}`;

  const num = (n) => (n < 0 ? `(${n})` : `${n}`);

  const minusBLatex = B < 0 ? `-(${B})` : `-${B}`;
  const denom = 2 * a;
  const denomFormulaLatex = `2\\cdot ${num(a)}`;
  const numMinus = -B - sqrtDisc;
  const numPlus = -B + sqrtDisc;

  const rootFromMinus = numMinus / denom;
  const rootFromPlus = numPlus / denom;
  const x1UsesMinus = rootFromMinus <= rootFromPlus + tol;

  const x1Sign = x1UsesMinus ? "-" : "+";
  const x2Sign = x1UsesMinus ? "+" : "-";
  const x1Num = x1UsesMinus ? numMinus : numPlus;
  const x2Num = x1UsesMinus ? numPlus : numMinus;

const x1Lines = String.raw`
\begin{aligned}
x_1 &= \dfrac{${minusBLatex} ${x1Sign} ${sqrtDisc}}{${denomFormulaLatex}} \\[4pt]
x_1 &= \dfrac{${x1Num}}{${denom}} \\[4pt]
x_1 &= ${x1L}
\end{aligned}
`;

  const x2Lines = String.raw`
\begin{aligned}
x_2 &= \dfrac{${minusBLatex} ${x2Sign} ${sqrtDisc}}{${denomFormulaLatex}} \\[4pt]
x_2 &= \dfrac{${x2Num}}{${denom}} \\[4pt]
x_2 &= ${x2L}
\end{aligned}
`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      {/* KROK 1 */}
      <p>
        <strong>Krok 1.</strong> Przenosimy wszystko na jedną stronę, aby po
        prawej stronie było <InlineMath math="0" />.
      </p>
      <BlockMath
        math={`${prettyLatex}\\quad \\Longleftrightarrow \\quad ${standardLatex}`}
      />

      {/* KROK 2 – delta i pierwiastki */}
      <p>
        <strong>Krok 2.</strong> Obliczamy miejsca zerowe z delty.
      </p>
      <BlockMath math={String.raw`\Delta = b^2 - 4ac`} />
      <BlockMath
        math={String.raw`\Delta = ${num(B)}^2 - 4\cdot ${num(a)}\cdot ${num(
          C
        )} = ${disc}`}
      />
      <BlockMath math={String.raw`\sqrt{\Delta} = ${sqrtDisc}`} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1rem",
          alignItems: "flex-start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={"x_1"} />
          </div>
          <BlockMath math={x1Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={"x_2"} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
      </div>

      {/* KROK 3 – znak trójmianu */}
      <p>
        <strong>Krok 3.</strong> Rysujemy szkic paraboli{" "}
        <InlineMath math={quadraticLatex(a, B, C)} />. Ponieważ{" "}
        <InlineMath math={`a=${a}`} /> ({armsTxt}), {whereTxt}. {inclusiveNote}
      </p>

      <div style={{ maxWidth: 520, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={aPos ? 1 : -1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          labelLeftTex={x1L}
          labelRightTex={x2L}
          highlightMode={solution.kind === "outside" ? "outside" : "between"}
          highlightLeftClosed={inclusive}
          highlightRightClosed={inclusive}
          highlightColor="#a78bfa"
        />
      </div>

      <p style={{ textAlign: "center" }}>
        {solution.kind === "outside"
          ? "Odczytujemy fragmenty wykresu leżące na zewnątrz miejsc zerowych."
          : "Odczytujemy fragment wykresu leżący pomiędzy miejscami zerowymi."}
      </p>

      {/* KROK 4 – zapis przedziału + oś */}
      <p>
        <strong>Krok 4.</strong> Zapisujemy zbiór rozwiązań i zaznaczamy go na
        osi liczbowej.
      </p>

      <div style={{ maxWidth: 520, margin: "0.3rem auto" }}>
        <BlockMath math={solLatex} />
      </div>

      <div style={{ maxWidth: 520, margin: "0.3rem auto" }}>
        {solution.kind === "outside" ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to: r1, rightClosed: inclusive, rightLabelLatex: x1L },
              { from: r2, leftClosed: inclusive, leftLabelLatex: x2L },
            ]}
          />
        ) : (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              {
                from: r1,
                to: r2,
                leftClosed: inclusive,
                rightClosed: inclusive,
                leftLabelLatex: x1L,
                rightLabelLatex: x2L,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
